import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../hooks/useAuth.js";
import { useNavigate } from "react-router-dom";
import api from "../services/api.js";

import Header from "../components/Header.jsx";
import NotificationsTab from "../components/NotificationsTab.jsx";
import RecordDetails from "../components/RecordDetails.jsx";
import ScheduleCalendar from "../components/ScheduleCalendar.jsx";
import Pagination from "../components/Pagination.jsx";

import { Bell, Clock, ClipboardList, RefreshCw } from "lucide-react";
import {
  fetchNotifications,
  fetchRecordById,
  fetchWfpQueue,
  selectPaymentRedeemerError,
  selectPaymentRedeemerLimit,
  selectPaymentRedeemerLoading,
  selectPaymentRedeemerNotifications,
  selectPaymentRedeemerNotificationsError,
  selectPaymentRedeemerNotificationsLoading,
  selectPaymentRedeemerPage,
  selectPaymentRedeemerRecords,
  selectPaymentRedeemerSearchTerm,
  selectPaymentRedeemerSelectedRecord,
  selectPaymentRedeemerTotal,
  selectPaymentRedeemerUser,
  selectPaymentRedeemerViewMode,
  setLimit,
  setPage,
  setSearchTerm,
  setSelectedRecord,
  setUser,
  setViewMode,
  updateRecord,
} from "../store/paymentRedeemerSlice.js";

const PaymentRedeemerPage = () => {
  const { user: authedUser, logout, loading: authLoading } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(selectPaymentRedeemerUser);
  const records = useSelector(selectPaymentRedeemerRecords);
  const total = useSelector(selectPaymentRedeemerTotal);
  const page = useSelector(selectPaymentRedeemerPage);
  const limit = useSelector(selectPaymentRedeemerLimit);
  const notifications = useSelector(selectPaymentRedeemerNotifications);
  const notificationsLoading = useSelector(selectPaymentRedeemerNotificationsLoading);
  const notificationsError = useSelector(selectPaymentRedeemerNotificationsError);
  const loading = useSelector(selectPaymentRedeemerLoading);
  const error = useSelector(selectPaymentRedeemerError);
  const selectedRecord = useSelector(selectPaymentRedeemerSelectedRecord);
  const viewMode = useSelector(selectPaymentRedeemerViewMode);
  const searchTerm = useSelector(selectPaymentRedeemerSearchTerm);

  const lastWfpQueryRef = useRef("");
  const recordCache = useMemo(() => {
    const map = {};
    records.forEach((record) => {
      if (record?._id) {
        map[record._id] = record;
      }
    });
    return map;
  }, [records]);

  // Keep local user synced
  useEffect(() => {
    dispatch(setUser(authedUser));
  }, [authedUser, dispatch]);

  // Header profile update callback
  const handleUpdateUser = (updatedUserData) => {
    dispatch(setUser(updatedUserData));
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Payment Redeemer queue fetch:
  // Backend filters by role:
  // - assignedPaymentRedeemer = me
  // - latest comment status = wfp
  const loadWfpQueue = useCallback(async () => {
    if (!user?.userId) return;
    const queryKey = `${user.userId}|${page}|${limit}|${searchTerm.trim()}`;
    if (lastWfpQueryRef.current === queryKey) {
      return;
    }
    lastWfpQueryRef.current = queryKey;

    try {
      await dispatch(fetchWfpQueue()).unwrap();
    } catch (err) {
      console.error("Error fetching payment redeemer records:", err);
      const msg = err?.message || "Failed to fetch Waiting for Payment records.";
      if (err?.status === 401) {
        dispatch(setSelectedRecord(null));
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else {
        console.error(msg);
      }
    }
  }, [user, dispatch, logout, navigate, page, limit, searchTerm]);

  // Notifications fetch (new assignment + upcoming WFP follow-ups)
  const loadNotifications = useCallback(async () => {
    if (!user?.userId) return;

    try {
      await dispatch(fetchNotifications()).unwrap();
    } catch (err) {
      console.error("Error fetching notifications:", err);
      console.error(err?.message || "Failed to fetch notifications.");
    } finally {
    }
  }, [user, dispatch]);

  // Guard
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [authLoading, user, navigate]);

  // Load WFP queue when on records tab
  useEffect(() => {
    if (!authLoading && user?.userId && !selectedRecord && viewMode === "records") {
      loadWfpQueue();
    }
  }, [authLoading, user, selectedRecord, page, limit, viewMode, loadWfpQueue]);

  // Poll notifications always (like collector behavior)
  useEffect(() => {
    if (!authLoading && user?.userId) {
      loadNotifications();
      const id = setInterval(loadNotifications, 60000);
      return () => clearInterval(id);
    }
    return undefined;
  }, [authLoading, user, loadNotifications]);

  // Debounce search -> reset page
  useEffect(() => {
    const id = setTimeout(() => {
      if (viewMode === "records") dispatch(setPage(1));
    }, 300);
    return () => clearTimeout(id);
  }, [dispatch, searchTerm, viewMode]);

  // Details: ALWAYS fetch full record
  const handleOpenDetails = async (record) => {
    try {
      if (record?._id) {
        dispatch(setSelectedRecord(record));
        await dispatch(fetchRecordById(record._id)).unwrap();
      }
    } catch (err) {
      console.error("Failed to open record details:", err);
    }
  };

  // Open from notification
  const handleNotificationSelect = async (recordId) => {
    try {
      await dispatch(fetchRecordById(recordId)).unwrap();
    } catch (err) {
      console.error("Failed to open record from notification:", err);
    }
  };

  // Save record (same pattern as collector)
  const handleSaveRecord = async (id, data) => {
    try {
      const updated = await dispatch(updateRecord({ id, data })).unwrap();
      dispatch(setSelectedRecord(updated));
      if (viewMode === "records") {
        lastWfpQueryRef.current = "";
        loadWfpQueue();
      }
      loadNotifications();
      return updated;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  if (authLoading) return <div className="text-center p-8">Loading authentication...</div>;
  if (!user) return <div className="text-center p-8">Redirecting to login...</div>;

  const navItems = [
    { label: "WFP Records", icon: <ClipboardList className="w-4 h-4" />, mode: "records" },
    { label: "Alerts", icon: <Bell className="w-4 h-4" />, mode: "notifications" },
    { label: "Schedule", icon: <Clock className="w-4 h-4" />, mode: "schedule" },
  ];

  return (
    <>
      <Header
        user={user}
        onLogout={handleLogout}
        api={api}
        onUpdateUser={handleUpdateUser}
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={(mode) => dispatch(setViewMode(mode))}
        overdueTaskCount={notifications.length} // ✅ badge count
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {selectedRecord ? (
            <RecordDetails
              record={selectedRecord}
              onBack={() => {
                dispatch(setSelectedRecord(null));
                if (viewMode === "records") loadWfpQueue();
                loadNotifications();
              }}
              onSave={handleSaveRecord}
              user={user}
              api={api}
            />
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 rounded-xl border-l-4 bg-red-50 border-red-500 text-red-800">
                  {error}
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {viewMode === "records" && (
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                      <h2 className="text-2xl font-semibold text-gray-800">
                        Waiting for Payment (WFP)
                      </h2>

                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Search by Provider, Patient, Adj…"
                          value={searchTerm}
                          onChange={(e) => dispatch(setSearchTerm(e.target.value))}
                          className="px-8 py-2 w-full sm:w-96 md:w-[448px] lg:w-[480px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        />

                        <button
                          onClick={() => {
                            lastWfpQueryRef.current = "";
                            loadWfpQueue();
                          }}
                          disabled={loading}
                          className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition disabled:bg-gray-400"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Refresh
                        </button>
                      </div>
                    </div>

                    {loading && records.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">Loading...</div>
                    ) : records.length > 0 ? (
                      <>
                        {loading && (
                          <div className="mb-3 text-sm text-gray-500">Refreshing records…</div>
                        )}
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Provider
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Patient
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Adj Number
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Bill
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Latest Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Actions
                                </th>
                              </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                              {records.map((r) => {
                                const latestStatus = r?.comments?.[0]?.status;

                                return (
                                  <tr key={r._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">{r.provider || "N/A"}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">{r.ptName || "N/A"}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {r.adjNumber?.[0]?.value || "N/A"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {typeof r.bill === "number"
                                        ? `$${r.bill.toLocaleString()}`
                                        : "N/A"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                                        {latestStatus === "wfp"
                                          ? "Waiting for Payment"
                                          : (latestStatus || "N/A")}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <button
                                        onClick={() => handleOpenDetails(r)}
                                        className="text-indigo-600 hover:text-indigo-900 font-medium"
                                      >
                                        Details
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <Pagination
                          page={page}
                          setPage={(value) => dispatch(setPage(value))}
                          limit={limit}
                          setLimit={(value) => dispatch(setLimit(value))}
                          total={total}
                        />
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        {searchTerm
                          ? "No records match your search."
                          : "No Waiting for Payment records assigned to you."}
                      </div>
                    )}
                  </div>
                )}

                {viewMode === "notifications" && (
                  <div className="p-6">
                    <NotificationsTab
                      notifications={notifications}
                      onRecordSelect={handleNotificationSelect}
                      loading={notificationsLoading}
                      error={notificationsError}
                    />
                  </div>
                )}

                {viewMode === "schedule" && (
                  <div className="p-6">
                    <ScheduleCalendar
                      user={user}
                      onRecordSelect={(record) => dispatch(setSelectedRecord(record))}
                      api={api}
                      recordCache={recordCache}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentRedeemerPage;
